<template>
  <div class="relative w-full">
    <!-- Left Icon Slot -->
    <span
      v-if="$slots['icon-left'] || leftIcon"
      class="absolute inset-y-0 left-3 flex items-center"
      :class="iconSize"
    >
      <slot name="icon-left">
        <component :is="leftIcon" class="text-gray-500" v-if="leftIcon"></component>
      </slot>
    </span>

    <!-- Label (Floating & Static) -->
    <label
      v-if="label"
      :for="id"
      :id="`${id}-label`"
      class="transition-all duration-200 ease-in-out"
      :class="[
        floatingLabel ? 'absolute left-4' : 'relative mb-2 block',
        floatingLabel && (hasValue || isFocused) ? labelSize.active : labelSize.inactive,
        !floatingLabel ? labelSize.static : '',
        labelClasses,
      ]"
    >
      {{ label }}
    </label>

    <!-- Select Button -->
    <div
      :id="id"
      :aria-labelledby="label ? `${id}-label` : null"
      @keydown="handleKeydown"
      @click="handleToggleDropdown"
      class="flex w-full cursor-pointer items-center justify-between rounded-[6px] bg-white"
      :class="[
        inputSize,
        inputPadding,
        borderClasses,
        inputTextColor,
        {
          'pt-4': floatingLabel && (hasValue || isFocused),
          'pl-10': $slots['icon-left'] || leftIcon,
        },
      ]"
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled ? 'true' : 'false'"
      @focus="isFocused = true"
      @blur="handleBlur"
      role="combobox"
      aria-autocomplete="none"
      :aria-controls="`${id}-listbox`"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-activedescendant="
      isOpen && options[focusedIndex]
        ? `${id}-option-${focusedIndex}`
        : null
    ">
      <div class="truncate">
        {{ selectedOption ? selectedOption.label : placeholder }}
      </div>
      <div class="flex items-center">
        <IconChevronDown
          class="transition-transform duration-200"
          :class="{ 'rotate-180 transform': isOpen }"
        />
      </div>
    </div>

    <!-- Options Dropdown -->
    <div
      v-show="isOpen"
      :id="`${id}-listbox`"
      class="border-gray-200 absolute left-0 right-0 z-10 mt-1 max-h-60 overflow-auto rounded-md border bg-white shadow-lg"
      role="listbox"
    >
      <div
        v-for="(option, index) in options"
        :key="option.value"
        :id="`${id}-option-${index}`"
        class="cursor-pointer px-4 py-2 text-black hover:bg-turquoise-10"
        :class="{
          'bg-turquoise-20': selectedOption && focusedIndex === index && selectedOption.value === option.value,
          'bg-turquoise-10': focusedIndex === index
        }"
        role="option"
        :aria-selected="selectedOption && selectedOption.value === option.value"
        @click="handleSelectOption(option)"
      >
        {{ option.label }}
      </div>
      <div v-if="options.length === 0" class="text-gray-500 px-4 py-2 italic">
        No options available
      </div>
    </div>
  </div>
</template>

<script>
import IconChevronDown from '../../icons/IconChevronDown.vue';

export default {
  name: 'SelectField',
  components: {
    IconChevronDown,
  },
  props: {
    modelValue: [String, Number, Object],
    id: String,
    label: String,
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    disabled: Boolean,
    floatingLabel: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
      // Each option should have { label: String, value: Any }
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    leftIcon: [String, Object],
    labelClasses: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isFocused: false,
      isOpen: false,
      selectedOption: null,
      focusedIndex: 0,
    };
  },
  computed: {
    hasValue() {
      return this.selectedOption !== null;
    },
    borderClasses() {
      return {
        'border-gray border border-solid hover:bg-turquoise-10 hover:border-turquoise-40 focus:border-turquoise-60 focus:border-2':
          this.status === '' || !this.status,
        'border-red-500 focus:border-red-500': this.status === 'error',
        'border-yellow-500 focus:border-yellow-500': this.status === 'warning',
        'border-green-500 focus:border-green-500': this.status === 'success',
        'cursor-not-allowed opacity-50': this.disabled,
      };
    },
    inputTextColor() {
      return this.selectedOption ? 'text-[#000]' : 'text-gray-400';
    },
    inputSize() {
      return {
        sm: 'h-8 text-sm',
        md: 'h-12 text-base',
        lg: 'h-14 text-lg',
      }[this.size || 'lg'];
    },
    inputPadding() {
      return {
        sm: 'px-2',
        md: 'px-4',
        lg: 'px-4',
      }[this.size || 'lg'];
    },
    iconSize() {
      return {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
      }[this.size || 'lg'];
    },
    labelSize() {
      return {
        active: {
          sm: 'text-form-label text-[#5E5E5E] top-[10px]',
          md: 'text-form-label text-[#5E5E5E] top-[10px]',
          lg: 'text-form-label text-[#5E5E5E] top-[10px]',
        }[this.size || 'lg'],
        inactive: {
          sm: 'text-form-input text-[#5E5E5E] top-4',
          md: 'text-form-input text-[#5E5E5E] top-4',
          lg: 'text-form-input text-[#5E5E5E] top-4',
        }[this.size || 'lg'],
        static: {
          sm: 'text-form-label text-[#5E5E5E] top-0',
          md: 'text-form-label text-[#5E5E5E] top-0',
          lg: 'text-form-label text-[#5E5E5E] top-0',
        }[this.size || 'lg'],
      };
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (newValue !== undefined && newValue !== null) {
          this.setSelectedFromModelValue(newValue);
        } else {
          this.selectedOption = null;
        }
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    if (this.modelValue !== undefined && this.modelValue !== null) {
      this.setSelectedFromModelValue(this.modelValue);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    handleBlur() {
      if (!this.hasValue) {
        this.isFocused = false;
      }
    },
    handleToggleDropdown() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          this.focusedIndex = this.selectedOption
            ? this.options.findIndex(o => o.value === this.selectedOption.value)
            : 0;
          this.isFocused = true;
        }
      }
    },
    handleSelectOption(option) {
      this.selectedOption = option;
      this.isOpen = false;
      this.$emit('update:modelValue', option.value);
      this.$emit('change', option);
    },
    handleOutsideClick(event) {
      const isClickInside = this.$el.contains(event.target);
      if (!isClickInside && this.isOpen) {
        this.isOpen = false;
        if (!this.hasValue) {
          this.isFocused = false;
        }
      }
    },
    handleKeydown(e) {
      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        this.isOpen = true;
        this.focusedIndex = 0;
        e.preventDefault();
      } else if (this.isOpen) {
        if (e.key === 'ArrowDown') {
          this.focusedIndex = (this.focusedIndex + 1) % this.options.length;
          e.preventDefault();
        } else if (e.key === 'ArrowUp') {
          this.focusedIndex = (this.focusedIndex - 1 + this.options.length) % this.options.length;
          e.preventDefault();
        } else if (e.key === 'Enter') {
          if (this.focusedIndex >= 0) this.handleSelectOption(this.options[this.focusedIndex]);
          e.preventDefault();
        } else if (e.key === 'Escape') {
          this.isOpen = false;
          e.preventDefault();
        }
      }
    },
    setSelectedFromModelValue(value) {
      const option = this.options.find((opt) => opt.value === value);
      if (option) {
        this.selectedOption = option;
      } else if (
        typeof value === 'object' &&
        value.hasOwnProperty('label') &&
        value.hasOwnProperty('value')
      ) {
        this.selectedOption = value;
      }
    },
  },
};
</script>
